import {
  Button,
  Input,
  Label,
  ModalContent,
  ModalFooter,
} from '@sandtech-hub-monorepo/ui-kit';
import React, { useMemo, useState } from 'react';
import { InfoIcon, Loader2Icon, MinusCircleIcon, PlusIcon } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { UpdateUserDocument } from '@sandtech-hub-monorepo/ehub-client';
import { z } from 'zod';

interface Props {
  close: () => void;
  defaultUrls: string[];
}

export const PortfolioAndProjectsEditModal = ({
  close,
  defaultUrls,
}: Props) => {
  const [urls, setUrls] = useState<string[]>(defaultUrls);
  const errors = useMemo(
    () =>
      urls.map((url) => {
        // Empty rows are dropped on submit rather than saved, so they aren't
        // "invalid" - just not filled in yet. Validating them anyway meant a
        // freshly-added blank row showed "Invalid url" before the learner had
        // typed anything, and blocked saving the other, already-valid URLs.
        if (!url) {
          return '';
        }
        const result = z.string().url().max(200).safeParse(url);
        if (!result.success) {
          return result.error.errors[0].message;
        }
        return '';
      }),
    [urls]
  );
  const [mutate, { loading }] = useMutation(UpdateUserDocument);

  const dirty = useMemo(
    () => urls.filter((v) => !!v).toString() !== defaultUrls.toString(),
    [urls, defaultUrls]
  );

  const submit = async () => {
    await mutate({
      variables: {
        input: {
          portfolioAndProjects: urls.filter((v) => !!v),
        },
      },
    });
    close();
  };

  return (
    <ModalContent
      title="Portfolio & Projects"
      description="Add your portfolio and projects links"
    >
      <div className="p-2 rounded-lg bg-background-quaternary flex items-center gap-3">
        <InfoIcon className="block h-6 w-6 text-primary" />
        <span className="text-xs flex-1 leading-[1.5]">
          <b className="font-semibold">Showcase Your Work</b>: Enhance your
          profile by adding a link to your professional portfolio. Whether it's
          GitHub for your coding projects, Behance for your design marvels, or
          Dribbble for your creative shots, sharing your portfolio is a great
          way to highlight your skills and projects to potential collaborators
          and employers
        </span>
      </div>
      {urls.map((url, i) => (
        <div key={i} className="mt-2">
          <Label>Url</Label>
          <div className="flex items-center">
            <Input
              className="flex-1"
              value={url}
              onChange={(e) =>
                setUrls((v) =>
                  v.map((u, iu) => (i === iu ? e.target.value : u))
                )
              }
            />
            <MinusCircleIcon
              className="h-5 cursor-pointer"
              onClick={() => setUrls((v) => v.filter((_, iu) => i !== iu))}
              size={40}
            />
          </div>
          {errors[i] && <p className="text-destructive text-sm">{errors[i]}</p>}
        </div>
      ))}

      {urls.length <= 6 && (
        <div className="flex justify-end">
          <Button
            className="flex gap-1"
            variant="link"
            onClick={() => setUrls((v) => [...v, ''])}
          >
            <PlusIcon size={16} /> Add URL
          </Button>
        </div>
      )}
      <ModalFooter>
        <Button variant="outline" onClick={close}>
          Cancel
        </Button>
        <Button
          onClick={submit}
          disabled={!dirty || loading || !!errors.find((e) => !!e)}
        >
          {loading ? <Loader2Icon className="animate-spin" /> : 'Save'}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};
